import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight,
  BookMarked,
  Check,
  CircleDollarSign,
  Clock3,
  FileText,
  History,
  MessageSquareText,
  ShieldCheck,
  Users,
} from "lucide-react";

const reveal = {
  initial: { opacity: 0, y: 14 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, amount: 0.25 },
  transition: { duration: 0.45, ease: [0.22, 1, 0.36, 1] },
};

const PilotLink = ({ dark = false, className = "" }) => (
  <Link
    to="/request-pilot"
    className={`inline-flex items-center justify-center gap-2 rounded-full px-5 py-3 text-sm font-medium transition-colors ${
      dark
        ? "bg-white text-[#171717] hover:bg-[#F4F1E8]"
        : "bg-[#171717] text-white hover:bg-[#2B2B2B]"
    } ${className}`}
  >
    Request a pilot <ArrowRight className="h-4 w-4" />
  </Link>
);

const ProductProof = () => (
  <div className="relative mx-auto w-full max-w-[620px]">
    <div className="absolute -inset-5 rounded-[32px] bg-[#E8E0C8]/55 blur-2xl" />
    <div className="relative overflow-hidden rounded-2xl border border-black/10 bg-[#FCFCFA] shadow-[0_30px_80px_rgba(23,23,23,0.13)]">
      <div className="flex items-center justify-between border-b border-black/10 px-5 py-4">
        <div className="flex items-center gap-2.5">
          <div className="grid h-8 w-8 place-items-center rounded-md bg-[#171717] text-white"><BookMarked className="h-4 w-4" /></div>
          <div>
            <div className="font-display text-lg leading-none">Meridian Health</div>
            <div className="mt-1 text-[9px] uppercase tracking-[0.18em] text-black/45">Current relationship truth</div>
          </div>
        </div>
        <div className="rounded-full bg-[#FCE7E7] px-3 py-1 font-mono text-[10px] text-[#A12B2B]">2 need attention</div>
      </div>

      <div className="grid grid-cols-3 border-b border-black/10 bg-white">
        {[
          ["Active", "5"],
          ["Outstanding", "$12,000"],
          ["Changed", "2 this week"],
        ].map(([label, value]) => (
          <div key={label} className="border-r border-black/10 px-4 py-4 last:border-r-0">
            <div className="text-[9px] uppercase tracking-[0.16em] text-black/40">{label}</div>
            <div className="mt-1 font-display text-xl text-[#171717]">{value}</div>
          </div>
        ))}
      </div>

      <div className="space-y-3 p-4">
        <div className="rounded-xl border border-[#D8A13B]/30 bg-[#FFF9E8] p-4">
          <div className="flex items-start gap-3">
            <div className="mt-0.5 grid h-8 w-8 shrink-0 place-items-center rounded-full bg-[#F6E7B9] text-[#8A5B08]"><Clock3 className="h-4 w-4" /></div>
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <span className="text-sm font-semibold">Proposal date changed</span>
                <span className="font-mono text-[10px] text-[#8A5B08]">AUG 30 → SEP 8</span>
              </div>
              <p className="mt-1 text-xs leading-relaxed text-black/55">“I will send the revised proposal next Tuesday instead.”</p>
            </div>
          </div>
        </div>
        <div className="rounded-xl border border-black/10 bg-white p-4">
          <div className="flex items-start gap-3">
            <div className="mt-0.5 grid h-8 w-8 shrink-0 place-items-center rounded-full bg-[#E6F4ED] text-[#176B49]"><CircleDollarSign className="h-4 w-4" /></div>
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <span className="text-sm font-semibold">Retainer payment</span>
                <span className="font-mono text-[10px] text-[#176B49]">$600 RECEIVED</span>
              </div>
              <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-black/5"><div className="h-full w-[38%] rounded-full bg-[#4C9B75]" /></div>
              <p className="mt-2 text-xs text-black/50">$1,000 remains outstanding, due next week</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
);

export default function Marketing() {
  return (
    <div className="min-h-screen bg-[#F5F2EA] text-[#171717] selection:bg-[#E8D8A9]" data-testid="marketing-page">
      <header className="border-b border-black/10">
        <div className="mx-auto flex max-w-[1440px] items-center justify-between px-6 py-5 lg:px-10">
          <Link to="/" className="flex items-center gap-3">
            <div className="grid h-9 w-9 place-items-center rounded-md bg-[#171717] text-white"><BookMarked className="h-4 w-4" /></div>
            <div className="font-display text-xl">Commitment Ledger</div>
          </Link>
          <div className="flex items-center gap-3">
            <Link to="/app" className="hidden rounded-full px-4 py-2 text-sm text-black/60 hover:bg-black/5 hover:text-black sm:inline-flex">Open product</Link>
            <PilotLink className="!px-4 !py-2" />
          </div>
        </div>
      </header>

      <main>
        <section className="overflow-hidden border-b border-black/10">
          <div className="mx-auto grid max-w-[1440px] items-center gap-14 px-6 py-20 lg:grid-cols-[1.05fr_0.95fr] lg:px-10 lg:py-28">
            <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.55 }}>
              <div className="inline-flex items-center gap-2 rounded-full border border-black/10 bg-white/50 px-3 py-1.5 text-[10px] uppercase tracking-[0.18em] text-black/55">
                <ShieldCheck className="h-3.5 w-3.5" /> Built for client-facing teams
              </div>
              <h1 className="mt-7 max-w-4xl font-display text-[clamp(3.3rem,6.2vw,6.6rem)] leading-[0.94] tracking-[-0.045em]">
                Your clients remember the promise. Does your team?
              </h1>
              <p className="mt-7 max-w-2xl text-lg leading-relaxed text-black/60 md:text-xl">
                Commitment Ledger keeps a living, evidence-backed record of what clients promised your team, what your team promised back, and what changed afterward.
              </p>
              <div className="mt-9 flex flex-col gap-3 sm:flex-row">
                <PilotLink />
                <Link to="/app" className="inline-flex items-center justify-center gap-2 rounded-full border border-black/15 bg-white/50 px-5 py-3 text-sm font-medium hover:bg-white">
                  Explore the product <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
              <p className="mt-5 text-xs text-black/40">Four-week assisted pilot for agencies and consultancies. No inbox integration required.</p>
            </motion.div>
            <motion.div initial={{ opacity: 0, x: 18 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.55, delay: 0.1 }}>
              <ProductProof />
            </motion.div>
          </div>
        </section>

        <section className="border-b border-black/10 bg-[#171717] text-white">
          <div className="mx-auto grid max-w-[1440px] gap-8 px-6 py-14 md:grid-cols-[0.7fr_1.3fr] lg:px-10">
            <div className="text-[11px] uppercase tracking-[0.22em] text-white/45">The operational gap</div>
            <p className="max-w-4xl font-display text-3xl leading-tight text-white/90 md:text-5xl">
              The agreement is in one thread. The revision is in another. The action lives in someone&rsquo;s head.
            </p>
          </div>
        </section>

        <motion.section {...reveal} className="mx-auto max-w-[1440px] px-6 py-24 lg:px-10 lg:py-32">
          <div className="grid gap-12 lg:grid-cols-[0.8fr_1.2fr]">
            <div>
              <div className="text-[11px] uppercase tracking-[0.22em] text-black/45">Not another task extractor</div>
              <h2 className="mt-4 font-display text-4xl leading-tight md:text-6xl">Current truth, not another list to maintain.</h2>
            </div>
            <div className="grid gap-px overflow-hidden rounded-2xl border border-black/10 bg-black/10 md:grid-cols-3">
              {[
                [MessageSquareText, "Capture", "Important promises are identified from selected conversations with the original words attached."],
                [History, "Reconcile", "Later messages revise, block, cancel, partially fulfil, or complete the existing commitment."],
                [Check, "Act", "Only the few items that could delay work, cash, or decisions rise to the top."],
              ].map(([Icon, title, copy], index) => (
                <div key={title} className="bg-[#FAF8F2] p-7">
                  <div className="flex items-center justify-between"><Icon className="h-5 w-5" /><span className="font-mono text-[10px] text-black/35">0{index + 1}</span></div>
                  <h3 className="mt-10 font-display text-2xl">{title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-black/55">{copy}</p>
                </div>
              ))}
            </div>
          </div>
        </motion.section>

        <section className="border-y border-black/10 bg-[#EBE5D7]">
          <div className="mx-auto grid max-w-[1440px] gap-12 px-6 py-24 lg:grid-cols-2 lg:px-10 lg:py-32">
            <motion.div {...reveal} className="rounded-2xl border border-black/10 bg-[#FAF8F2] p-7 md:p-9">
              <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-black/45"><Users className="h-4 w-4" /> Every client relationship</div>
              <h2 className="mt-5 font-display text-4xl">Promises move in both directions.</h2>
              <div className="mt-8 grid gap-3 sm:grid-cols-2">
                <div className="rounded-xl bg-white p-5">
                  <div className="text-[10px] uppercase tracking-[0.17em] text-black/40">Promised to us</div>
                  <div className="mt-4 text-sm font-medium">Final approval</div>
                  <div className="mt-2 text-xs text-black/45">Client · due Friday</div>
                </div>
                <div className="rounded-xl bg-[#171717] p-5 text-white">
                  <div className="text-[10px] uppercase tracking-[0.17em] text-white/40">Promised by us</div>
                  <div className="mt-4 text-sm font-medium">Revised launch plan</div>
                  <div className="mt-2 text-xs text-white/45">Your team · due tomorrow</div>
                </div>
              </div>
            </motion.div>
            <motion.div {...reveal} className="flex flex-col justify-center lg:pl-12">
              <div className="text-[11px] uppercase tracking-[0.22em] text-black/45">Accountability without blame</div>
              <h2 className="mt-4 font-display text-4xl leading-tight md:text-6xl">See the whole relationship before the status call.</h2>
              <p className="mt-6 max-w-xl text-base leading-relaxed text-black/60">Client pages bring deadlines, outstanding money, recent changes, and source evidence together. Your team arrives knowing what is current and what needs a decision.</p>
            </motion.div>
          </div>
        </section>

        <motion.section {...reveal} className="mx-auto max-w-[1440px] px-6 py-24 lg:px-10 lg:py-32">
          <div className="grid items-end gap-10 border-b border-black/10 pb-14 md:grid-cols-2">
            <h2 className="font-display text-4xl leading-tight md:text-6xl">Every status has receipts.</h2>
            <p className="max-w-xl text-base leading-relaxed text-black/60 md:justify-self-end">The original quote stays attached. Revisions preserve what changed. Partial fulfilment retains the balance. You can explain why the ledger says what it says.</p>
          </div>
          <div className="grid gap-5 pt-10 md:grid-cols-3">
            {[
              [FileText, "Original evidence", "The exact words and source conversation remain visible."],
              [History, "Change history", "Previous and current deadlines are never collapsed into one date."],
              [CircleDollarSign, "Financial truth", "Original, fulfilled, and outstanding amounts stay separate."],
            ].map(([Icon, title, copy]) => (
              <div key={title} className="group rounded-xl border border-black/10 p-6 transition-colors hover:bg-white/60">
                <Icon className="h-5 w-5 text-black/55" />
                <h3 className="mt-8 font-display text-2xl">{title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-black/55">{copy}</p>
              </div>
            ))}
          </div>
        </motion.section>

        <section className="px-4 pb-4">
          <div className="mx-auto max-w-[1500px] overflow-hidden rounded-[28px] bg-[#171717] px-6 py-20 text-white md:px-14 md:py-24">
            <div className="grid items-end gap-12 md:grid-cols-[1fr_auto]">
              <div>
                <div className="text-[11px] uppercase tracking-[0.22em] text-white/40">Four-week assisted pilot</div>
                <h2 className="mt-5 max-w-4xl font-display text-5xl leading-[0.98] md:text-7xl">Bring one client relationship. Find out what your current process forgets.</h2>
                <p className="mt-6 max-w-2xl text-base leading-relaxed text-white/55">We will help capture selected conversations, review the ledger with you weekly, and measure which maintained commitments caused useful action.</p>
              </div>
              <PilotLink dark className="md:mb-1" />
            </div>
          </div>
        </section>
      </main>

      <footer className="mx-auto flex max-w-[1440px] flex-col gap-4 px-6 py-10 text-xs text-black/45 sm:flex-row sm:items-center sm:justify-between lg:px-10">
        <div>Commitment Ledger · Client promises kept current</div>
        <Link to="/app" className="hover:text-black">Open product</Link>
      </footer>
    </div>
  );
}
